import type { CSSProperties, ReactNode } from 'react';

export type CpmPageShellVariant = 'standard' | 'compactBottom' | 'masterEditor';

interface CpmPageShellProps {
    children: ReactNode;
    /** Column gap between major page sections. Design: VQ 20, Overview 24, Excise/Volume 22. */
    gap?: 14 | 20 | 22 | 24;
    /** Max content width. Design: most screens 1280, Volume 1080, Merge 1200, Master Editor / VQ 1420, Config Admin 1440. */
    maxWidth?: 1080 | 1200 | 1280 | 1360 | 1420 | 1440;
    /**
     * Page padding in absolute px (requires Layout flush — no rem cancel).
     * - standard: 28/28/64 (Overview)
     * - compactBottom: 28/28/72 (Excise, Volume)
     * - masterEditor: 24/24/40 (Master Editor, Verification Queue)
     */
    variant?: CpmPageShellVariant;
    className?: string;
    /**
     * Replaces the variant padding. Access-management pages pass the prototype
     * inset (internal people 22/34/40, starters 26/34/60). Other pages omit it.
     */
    padding?: string;
}

const VARIANT_PAD: Record<CpmPageShellVariant, CSSProperties> = {
    standard: { padding: '28px 28px 64px' },
    compactBottom: { padding: '28px 28px 72px' },
    masterEditor: { padding: '24px 24px 40px' },
};

/**
 * Shared CPM page chrome. Use with `<Layout flush>` so this is the only page inset.
 */
export default function CpmPageShell({
    children,
    gap = 20,
    maxWidth = 1280,
    variant = 'standard',
    className = '',
    padding,
}: CpmPageShellProps) {
    return (
        <div
            className={['cpm-theme', className].filter(Boolean).join(' ')}
            style={{
                minHeight: 'calc(100vh - 64px)',
                backgroundColor: 'var(--cpm-page-bg)',
                boxSizing: 'border-box',
                width: '100%',
                ...(padding ? { padding } : VARIANT_PAD[variant]),
            }}
        >
            <div
                style={{
                    maxWidth,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap,
                    width: '100%',
                    boxSizing: 'border-box',
                }}
            >
                {children}
            </div>
        </div>
    );
}
